import keyBy from "lodash/keyBy";
import orderBy from "lodash/orderBy";
import React, { useEffect, useMemo } from "react";
import { Controller, FieldArrayWithId, UseFormReturn, useFieldArray } from "react-hook-form";

import { CurrencyInput, Autocomplete, IconButton } from "@puzzle/ui";
import { Close } from "@puzzle/icons";

import { PayrollJournalMapItemCategory } from "graphql/types";

import { StyledPayrollTable, TH, TR, TD } from "./styles";
import { CompanyBenefitFragment, PayrollItemFragment } from "./graphql.generated";
import { shouldHighlight } from "./TransparencyTable";
import { PayrollFormValues, UpdateMode } from "./UpsertPayrollDrawer";
import { usePayrollPage } from "./PayrollProvider";
import { Box } from "@puzzle/ve";

function filterBeneitsForCategory({
  companyLevelBenefits,
  category,
  addedBenefits,
}: {
  category?: PayrollJournalMapItemCategory;
  companyLevelBenefits: CompanyBenefitFragment[];
  addedBenefits: FieldArrayWithId<PayrollFormValues, "rows", "rowId">[];
}) {
  if (!category) return companyLevelBenefits;

  const existingForCategory = keyBy(
    addedBenefits.filter((b) => b.category === category),
    "benefitId"
  );

  return companyLevelBenefits.filter(({ id }) => {
    return !existingForCategory[id];
  });
}

export const UpsertPayrollTable = ({
  activePayroll,
  form,
  updateMode,
}: {
  activePayroll?: PayrollItemFragment;
  form: UseFormReturn<PayrollFormValues, PayrollFormValues>;
  updateMode: UpdateMode;
}) => {
  const { companyLevelBenefits } = usePayrollPage();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "rows",
    keyName: "rowId",
  });

  const formRows = useMemo(() => {
    const templatePayroll: Pick<
      PayrollItemFragment,
      "journalMapping" | "benefitDetails" | "netPay" | "employeeTaxes" | "employerTaxes"
    > = {
      journalMapping: { items: [], memoLines: [], relatedTransactions: [], summaryItems: [] },
      benefitDetails: [],
      netPay: "0",
      employeeTaxes: "0",
      employerTaxes: "0",
    };

    const payrollForForm =
      updateMode === "create" ? templatePayroll : (activePayroll ?? templatePayroll);

    const rows: PayrollFormValues["rows"] = [
      {
        name: "Salary",
        key: "salaries",
        amount: parseFloat(
          payrollForForm?.journalMapping.items.find(({ itemName }) => itemName === "Salaries")
            ?.amount ?? "0"
        ),
        category: PayrollJournalMapItemCategory.Employee,
      },

      {
        name: "Net pay",
        key: "netPay",
        amount: parseFloat(
          payrollForForm?.journalMapping.items.find(({ itemName }) => itemName === "Net pay")
            ?.amount ?? "0"
        ),
        category: PayrollJournalMapItemCategory.Employee,
      },

      {
        name: "Taxes",
        key: "employeeTaxes",
        amount: parseFloat(payrollForForm.employeeTaxes),
        category: PayrollJournalMapItemCategory.Employee,
      },

      {
        name: "Taxes",
        key: "employerTaxes",
        amount: parseFloat(payrollForForm.employerTaxes),
        category: PayrollJournalMapItemCategory.Employer,
      },

      // Employee benefits
      ...payrollForForm.benefitDetails.map((benefit) => {
        return {
          name: benefit.name,
          benefitId: benefit.id,
          amount: parseFloat(benefit.employeePortion),
          category: PayrollJournalMapItemCategory.Employee,
        };
      }),

      // Employer benefits
      ...payrollForForm.benefitDetails.map((benefit) => {
        return {
          name: benefit.name,
          benefitId: benefit.id,
          amount: parseFloat(benefit.employerPortion),
          category: PayrollJournalMapItemCategory.Employer,
        };
      }),

      {
        name: "Independent contractors",
        key: "contractorPaymentAmount",
        amount: parseFloat(
          payrollForForm?.journalMapping.items.find(
            ({ itemName }) => itemName === "Contractor payments"
          )?.amount ?? "0"
        ),
        category: PayrollJournalMapItemCategory.Contractor,
      },
    ];

    return rows;
  }, [activePayroll, updateMode]);

  // Hydrate table form with existing payroll
  useEffect(() => {
    form.reset({
      rows: formRows,
    });

    form.trigger();
  }, [form, formRows]);

  const orderedFields = useMemo(() => orderBy(fields, "category"), [fields]);
  const benefits =
    updateMode === "edit" && activePayroll ? activePayroll.companyBenefits : companyLevelBenefits;

  return (
    <StyledPayrollTable css={{ tableLayout: "fixed" }}>
      <thead>
        <TR>
          <TH colSpan={1}></TH>
          <TH colSpan={2}>Enter payroll details here</TH>
        </TR>
        <TR variant="subheader">
          <TH></TH>
          <TH>Item</TH>
          <TH>Enter amount</TH>
        </TR>
      </thead>
      <tbody>
        {orderedFields.map((curr, i) => {
          const prev = orderedFields[i - 1];
          const next = orderedFields[i + 1];
          const showCategory = i === 0 || (prev && curr.category !== prev.category);
          const isLastOfCategory = (next && curr.category !== next.category) || !next;
          const matchingIndex = fields.findIndex(({ rowId }) => rowId === curr.rowId);
          const filteredBenefits = isLastOfCategory // only calculate when at the end of the current category
            ? filterBeneitsForCategory({
                companyLevelBenefits: benefits,
                category: curr.category,
                addedBenefits: orderedFields.filter(({ benefitId }) => !!benefitId),
              })
            : [];
          const showAddRow =
            filteredBenefits.length > 0 &&
            curr.category !== PayrollJournalMapItemCategory.Contractor;

          return (
            <React.Fragment key={`${curr.benefitId}--${curr.rowId}`}>
              <TR isLastOfGroup={isLastOfCategory && !showAddRow}>
                <TD>{showCategory && curr.category}</TD>
                <TD
                  hasNext
                  highlighted={shouldHighlight({ itemName: curr.name, category: curr.category })}
                  bright={curr.category === null}
                >
                  {curr.name}
                </TD>
                <TD hasNext>
                  <Box css={{ display: "flex", alignItems: "center" }}>
                    <Controller
                      control={form.control}
                      name={`rows.${matchingIndex}.amount`}
                      render={({ field }) => {
                        return (
                          <CurrencyInput
                            size="mini"
                            placeholder=""
                            textAlign="right"
                            truncateWhenInactive={false}
                            ref={field.ref}
                            value={field.value === 0 ? undefined : field.value}
                            onValueChange={({ value }) => {
                              return field.onChange({
                                target: { value, name: field.name },
                              });
                            }}
                          />
                        );
                      }}
                    />
                    {curr.benefitId && (
                      <IconButton
                        css={{ width: "fit-content", paddingLeft: "8px" }}
                        onClick={() => remove(matchingIndex)}
                      >
                        <Close size={12} color="currentColor" />
                      </IconButton>
                    )}
                  </Box>
                </TD>
              </TR>
              {showAddRow && (
                <TR isLastOfGroup>
                  <TD></TD>
                  <TD colSpan={2}>
                    <Autocomplete<CompanyBenefitFragment, false, true, false>
                      placeholder="Add benefit"
                      disableClearable
                      hideInitialBorder
                      size="mini"
                      getOptionLabel={({ name }) => name}
                      getOptionKey={({ id }) => id}
                      options={filteredBenefits}
                      value={undefined}
                      onChange={(_, { name, id }) => {
                        append([
                          {
                            name,
                            benefitId: id,
                            amount: 0,
                            category: curr.category,
                          },
                        ]);
                      }}
                    />
                  </TD>
                </TR>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </StyledPayrollTable>
  );
};
